/* GrowthOS — Mass Update modal (multi-field builder) for selected accounts.
   Mirror of the leads/contacts mass-update modal UI; the updatable field set is
   driven by the real account column defs (`fields` prop = the same `allcolumnList`
   the legacy #myModal used, enriched in accounts.jsx with dropdown `data`:
   { field, headerName, type, data }). The backend updates one field per call
   (apiServices.updateMultipleAccountField({ ids, fieldName, fieldValue })), so each
   builder row fires its own call. Dropdown/relational fields send `<Field>Id` + the
   option id — same convention as the legacy modal. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import apiServices from '../../services/service';

const PATHS = {
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z',
  x: 'M18 6 6 18M6 6l12 12',
  chevD: 'M6 9l6 6 6-6',
  check: 'M20 6 9 17l-5-5',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  plus: 'M12 5v14M5 12h14',
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d={PATHS[name]} />
  </svg>
);

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
let _uid = 0;
const uid = () => 'mu' + ++_uid;

// reusable dropdown (button + popup, optional search)
const Dropdown = ({ value, placeholder, items, onChange, searchable }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const sel = items.find((i) => i.value === value);
  const list = searchable && q ? items.filter((i) => i.label.toLowerCase().includes(q.toLowerCase())) : items;
  return (
    <div className="gos-dd" ref={ref}>
      <button type="button" className={'gos-dd-btn' + (open ? ' open' : '') + (sel ? ' has-val' : '')} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="gos-dd-label">{sel ? sel.label : placeholder || 'Select…'}</span>
        <Ic name="chevD" size={14} style={{ flex: 'none', color: 'var(--gos-text-3)' }} />
      </button>
      {open && (
        <div className="gos-dd-pop">
          {searchable ? (
            <div className="gos-dd-search">
              <Ic name="search" size={14} />
              <input autoFocus placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
          ) : null}
          <div className="gos-dd-list">
            {list.length === 0 ? <div className="gos-dd-empty">No matches</div> : null}
            {list.map((i) => (
              <button key={i.value} type="button" className={'gos-dd-item' + (i.value === value ? ' on' : '')} onClick={() => { onChange(i.value); setOpen(false); setQ(''); }}>
                <span className="gos-dd-grow">{i.label}</span>
                {i.value === value ? <Ic name="check" size={14} style={{ color: 'var(--gos-accent)' }} /> : null}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AccountsMassUpdateModal = ({ onClose, accountIds = [], fields = [], onUpdated }) => {
  const [loading, setLoading] = useState(false);

  // Normalize the real account column defs into the builder's field model.
  const FIELDS = useMemo(
    () =>
      (fields || [])
        .filter((c) => c && c.field && c.headerName)
        .map((c) => {
          const type = c.type === 'checkbox' ? 'boolean' : c.type === 'number' ? 'number' : c.type === 'dropdown' ? 'dropdown' : 'text';
          const options =
            type === 'dropdown'
              ? (c.data || []).map((d) => ({
                  value: d.id != null ? d.id : d.userId,
                  label: d.displayName || d.display_name || d.name || '',
                }))
              : undefined;
          return { field: c.field, label: c.headerName, type, options };
        }),
    [fields]
  );
  const fieldDef = (id) => FIELDS.find((f) => f.field === id);
  const payloadName = (f) => (f.type === 'dropdown' ? cap(f.field) + 'Id' : cap(f.field));

  const [rows, setRows] = useState([{ key: uid(), field: '', value: '' }]);
  const usedFields = rows.map((r) => r.field).filter(Boolean);
  const setField = (key, fid) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, field: fid, value: fid && fieldDef(fid)?.type === 'boolean' ? false : '' } : r)));
  const setValue = (key, v) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, value: v } : r)));
  const addRow = () => setRows((rs) => [...rs, { key: uid(), field: '', value: '' }]);
  const removeRow = (key) => setRows((rs) => (rs.length === 1 ? [{ key: uid(), field: '', value: '' }] : rs.filter((r) => r.key !== key)));

  const valid = rows.filter((r) => {
    const f = fieldDef(r.field);
    if (!f) return false;
    if (f.type === 'boolean') return true;
    return String(r.value).trim() !== '';
  });
  const canAdd = usedFields.length < FIELDS.length && rows.every((r) => r.field);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  const submit = () => {
    if (valid.length === 0 || loading) return;
    setLoading(true);
    const calls = valid.map((r) => {
      const f = fieldDef(r.field);
      return apiServices.updateMultipleAccountField({
        ids: accountIds,
        fieldName: payloadName(f),
        fieldValue: String(r.value),
      });
    });
    Promise.all(calls)
      .then((responses) => {
        const ok = responses.every((res) => res && res.status === 200);
        if (ok) {
          toast.success(
            `Updated ${valid.length} field${valid.length > 1 ? 's' : ''} across ${accountIds.length} account${accountIds.length > 1 ? 's' : ''}`,
            { position: 'top-right' }
          );
          setLoading(false);
          if (onUpdated) onUpdated();
          onClose();
        } else {
          setLoading(false);
          const msgs = [];
          responses.forEach((res) => {
            for (const k in res?.errors || {}) {
              if (Object.prototype.hasOwnProperty.call(res.errors, k)) msgs.push(...res.errors[k]);
            }
          });
          if (msgs.length) msgs.forEach((m) => toast.error(m, { position: 'top-right' }));
          else toast.error('Some fields could not be updated.', { position: 'top-right' });
        }
      })
      .catch((error) => {
        const errs = error.response?.data?.errors;
        if (errs) Object.keys(errs).forEach((k) => errs[k].forEach((m) => toast.error(m, { position: 'top-right' })));
        else toast.error('An unexpected error occurred.', { position: 'top-right' });
        setLoading(false);
      });
  };

  const ValueInput = ({ row }) => {
    const f = fieldDef(row.field);
    if (!f) return <div className="gos-mu-value-empty">Select a field first</div>;
    if (f.type === 'dropdown') {
      return <Dropdown value={row.value} placeholder={'Choose ' + f.label.toLowerCase()} items={f.options || []} searchable={(f.options || []).length > 8} onChange={(v) => setValue(row.key, v)} />;
    }
    if (f.type === 'boolean') {
      return (
        <div className="gos-mu-bool">
          {[[true, 'Yes'], [false, 'No']].map(([val, lbl]) => (
            <button key={lbl} type="button" className={'gos-mu-bool-btn' + (row.value === val ? ' on' : '')} onClick={() => setValue(row.key, val)}>
              {lbl}
            </button>
          ))}
        </div>
      );
    }
    return (
      <input
        type={f.type === 'number' ? 'number' : 'text'}
        className="gos-tkt-input"
        placeholder={'Enter ' + f.label.toLowerCase()}
        value={row.value}
        onChange={(e) => setValue(row.key, e.target.value)}
      />
    );
  };

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}>
      <div className="gos-tkt-modal gos-mu-modal" role="dialog" aria-modal="true" aria-label="Mass update accounts">
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic"><Ic name="edit" size={17} /></span>
            <div>
              <div className="gos-import-title">Mass Update</div>
              <div className="gos-import-sub">Set one or more fields across the selected accounts</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onClose} aria-label="Close" disabled={loading}>
            <Ic name="x" size={18} />
          </button>
        </div>

        <div className="gos-tkt-body">
          {accountIds.length > 0 && (
            <div className="gos-tkt-scope">
              <Ic name="check" size={13} />
              Applies to <strong>{accountIds.length} selected account{accountIds.length > 1 ? 's' : ''}</strong>
            </div>
          )}
          <label className="gos-mu-fields-label">Fields to update</label>
          <div className="gos-mu-rows">
            {rows.map((r) => (
              <div className="gos-mu-row" key={r.key}>
                <div className="gos-mu-field">
                  <Dropdown
                    value={r.field}
                    placeholder="Select field"
                    searchable
                    items={FIELDS.filter((f) => f.field === r.field || !usedFields.includes(f.field)).map((f) => ({ value: f.field, label: f.label }))}
                    onChange={(fid) => setField(r.key, fid)}
                  />
                </div>
                <div className="gos-mu-val">
                  <ValueInput row={r} />
                </div>
                <button type="button" className="gos-mu-remove" onClick={() => removeRow(r.key)} title="Remove" aria-label="Remove field">
                  <Ic name="x" size={15} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className={'gos-mu-add' + (canAdd ? '' : ' dis')} onClick={() => canAdd && addRow()}>
            <Ic name="plus" size={15} /> Add another field
          </button>
        </div>

        <div className="gos-import-foot">
          <span className="faint" style={{ fontSize: 12.5, color: 'var(--gos-text-3)' }}>
            {valid.length ? `${valid.length} field${valid.length > 1 ? 's' : ''} ready` : 'No changes yet'}
          </span>
          <div className="gos-foot-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={submit} disabled={valid.length === 0 || loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Updating…
                </>
              ) : (
                <>
                  <Ic name="check" size={16} /> Update {accountIds.length} account{accountIds.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountsMassUpdateModal;
